/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useMemo } from 'react';

import { jsx, cssMap as unboundCssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import LinkButton from '@atlaskit/button/link';
import GlobeIcon from '@atlaskit/icon/core/globe';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import IconTile from '@atlaskit/icon/icon-tile';
import Link from '@atlaskit/link/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card/card/lazy';
import {
	ActionName,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
} from '@atlaskit/smart-card/enums';
import type { ActionItem, ElementItem } from '@atlaskit/smart-card/flexible/types';
import { FooterBlock } from '@atlaskit/smart-card/footer-block';
import { MetadataBlock } from '@atlaskit/smart-card/metadata-block';
import { SnippetBlock } from '@atlaskit/smart-card/snippet-block';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { TitleBlock } from '@atlaskit/smart-card/title-block';
import { token } from '@atlaskit/tokens';

import { extractUrlHostname } from '../../../extract-url-hostname';
import { logger } from '../../utils/logger';
import { AppIcon } from '../app-icon/app-icon';
import { ErrorBoundary } from '../error-boundary';
import { CardVariant } from './card-variant';
import { EMBEDDED_SOURCE_PRODUCTS } from './constants';
import type { KnowledgeCardSource } from './types';

const messages = defineMessages({
	navigate_button: {
		id: 'search_ai.action_card_navigate_button',
		defaultMessage: 'Navigate',
		description: 'Navigate button text for action card',
	},
});

const unboundStyles = unboundCssMap({
	compactContainer: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			width: '100%',
		},
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		maxWidth: '360px',
		minHeight: '72px',
		margin: '0 auto',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
		},
	},
	actionCardContainer: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		display: 'flex',
		alignItems: 'center',
		maxWidth: '360px',
		width: '100%',
		textDecoration: 'none',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	actionCardButton: {
		textAlign: 'right',
	},
	fallbackContainer: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	embeddedCardContainer: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="embed-card-resolved-view"]': {
			boxSizing: 'border-box',
			margin: '0',
			border: `1px solid ${token('color.border')}`,
			borderRadius: token('radius.large'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper > div[data-testid="embed-card-resolved-view"]::after': {
			outline: 'none',
		},
	},
	richSourceCardContainer: {
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-smart-link-container="true"]': {
			boxSizing: 'border-box',
			margin: '0',
			borderRadius: token('radius.large'),
		},
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-testid="smart-footer-block-resolved-view"]': {
			marginTop: 'auto',
		},
	},
	titleBlockCss: {
		gap: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			fontWeight: token('font.weight.semibold'),
			color: token('color.link'),
		},
	},
	compactTitleBlockCss: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		"[data-smart-element='Title']": {
			font: token('font.heading.xsmall'),
			marginBottom: token('space.025'),
		},
	},
	footerBlockCss: {
		height: '1.5rem',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		alignSelf: 'stretch',
	},
});

function constructSourceUrl({
	source,
	searchSessionId,
}: {
	source: KnowledgeCardSource;
	searchSessionId: string;
}): string {
	if (source.product === 'Confluence' && !!searchSessionId) {
		const url = new URL(source.url);
		url.searchParams.append('search_id', searchSessionId);
		return url.toString();
	}
	return source.url;
}

interface KnowledgeSmartCardProps {
	source: KnowledgeCardSource;
	variant?: CardVariant;
	index?: number;
	searchSessionId?: string;
	onClickHandler?: (params: any) => void;
}

interface SmartCardInternalProps {
	source: KnowledgeCardSource;
	index: number;
	searchSessionId: string;
	onClickHandler: (params: any) => void;
}

const createCardClickHandler =
	(
		source: KnowledgeCardSource,
		index: number,
		searchSessionId: string,
		onClickHandler: (params: any) => void,
		sourceCardType: string,
	) =>
	(e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType,
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

const createCardErrorHandler =
	(componentName: string) => (data: { status: string; err?: Error }) => {
		if (fg('kd_search_ai_robust_sentry_logs')) {
			if (data.status === 'errored') {
				void logger.warning({
					feature: 'knowledge-cards',
					element: 'smart-card',
					operation: 'resolve',
					category: 'network',
					error: data.err,
					message: 'Smart card failed to resolve',
				});
			}
			return;
		}
		logger.error({
			feature: 'knowledge-cards',
			element: 'smart-card',
			operation: 'render',
			category: 'render',
			error: new Error(`Error rendering ${componentName}`),
			functionName: componentName,
		});
	};

function SimpleFallbackCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	const handleClick = (e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType: 'compact',
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

	return (
		<Anchor
			href={source.url}
			xcss={unboundStyles.compactContainer}
			onClick={handleClick}
			testId="knowledge-smart-card-fallback"
		>
			<Box xcss={unboundStyles.fallbackContainer}>
				<Inline space="space.100" alignBlock="center">
					<AppIcon app={source.product} size="large" />
					<Stack space="space.025">
						<Text size="medium" weight="medium" maxLines={1}>
							{source.title || source.url}
						</Text>
						<Text size="small" color="color.text.subtle" maxLines={1}>
							{source.product || ''}
						</Text>
					</Stack>
				</Inline>
			</Box>
		</Anchor>
	);
}

function ActionCard({ source, index, searchSessionId, onClickHandler }: SmartCardInternalProps) {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={unboundStyles.actionCardContainer} testId="knowledge-smart-card-action-card">
			<Stack space="space.200" grow="fill">
				<Inline space="space.150" alignBlock="center">
					<IconTile label="" icon={GlobeIcon} appearance="gray" size="medium" />
					<Stack space="space.025">
						<Text weight="bold" size="medium" maxLines={1}>
							{source.title}
						</Text>
						<Text size="small" color="color.text.subtlest" maxLines={1}>
							<Link href={source.url} target="_blank" rel="noopener noreferrer" appearance="subtle">
								{source.url}
							</Link>
						</Text>
					</Stack>
				</Inline>
				<Box xcss={unboundStyles.actionCardButton}>
					<LinkButton
						href={source.url}
						iconAfter={LinkExternalIcon}
						onClick={createCardClickHandler(
							source,
							index,
							searchSessionId,
							onClickHandler,
							'action-card',
						)}
					>
						{formatMessage(messages.navigate_button)}
					</LinkButton>
				</Box>
			</Stack>
		</Box>
	);
}

function CompactKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary feature="knowledge-cards" element="smart-card" fallback={<Fragment />}>
			<Box xcss={unboundStyles.compactContainer} testId="knowledge-smart-card-compact">
				<CardSSR
					appearance="block"
					url={source.url}
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: true,
						hideBackground: true,
						hideElevation: true,
						size: SmartLinkSize.Medium,
						removeBlockRestriction: true,
					}}
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'compact',
					)}
					onError={createCardErrorHandler('CompactKnowledgeSmartCard')}
				>
					<TitleBlock
						maxLines={1}
						size={SmartLinkSize.Small}
						subtitle={[{ name: ElementName.Provider }]}
						position={SmartLinkPosition.Center}
						hideIcon={true}
						css={unboundStyles.compactTitleBlockCss}
					/>
				</CardSSR>
			</Box>
		</ErrorBoundary>
	);
}

function StandardKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	const footerActions: ActionItem[] = useMemo(
		() => [
			{ name: ActionName.FollowAction, hideIcon: true },
			{ name: ActionName.PreviewAction, hideIcon: true },
			{ name: ActionName.DownloadAction, hideIcon: true },
		],
		[],
	);

	const topMetadata: ElementItem[] = [
		{ name: ElementName.AuthorGroup },
		{ name: ElementName.AssignedTo },
		{ name: ElementName.CreatedBy },
		{ name: ElementName.ModifiedOn },
	];

	const titleMetadata: ElementItem[] = [{ name: ElementName.State }];

	return (
		<ErrorBoundary feature="knowledge-cards" element="smart-card" fallback={<Fragment />}>
			<Box xcss={unboundStyles.richSourceCardContainer}>
				<Card
					appearance="block"
					url={source.url}
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					ui={{ removeBlockRestriction: true }}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'rich-flexible',
					)}
					onError={createCardErrorHandler('StandardKnowledgeSmartCard')}
				>
					<TitleBlock maxLines={1} metadata={titleMetadata} css={unboundStyles.titleBlockCss} />
					<MetadataBlock primary={topMetadata} maxLines={1} />
					<SnippetBlock />
					<FooterBlock actions={footerActions} css={unboundStyles.footerBlockCss} />
				</Card>
			</Box>
		</ErrorBoundary>
	);
}

function EmbeddedKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary feature="knowledge-cards" element="smart-card" fallback={<Fragment />}>
			<Box xcss={unboundStyles.embeddedCardContainer}>
				<Card
					appearance="embed"
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					inheritDimensions={true}
					frameStyle="show"
					url={source.url}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'embedded',
					)}
					onError={createCardErrorHandler('EmbeddedKnowledgeSmartCard')}
				/>
			</Box>
		</ErrorBoundary>
	);
}

function DropdownSourceSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary feature="knowledge-cards" element="smart-card" fallback={<Fragment />}>
			<Card
				appearance="block"
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				url={source.url}
				onClick={createCardClickHandler(
					source,
					index,
					searchSessionId,
					onClickHandler,
					'dropdown-source',
				)}
				onError={createCardErrorHandler('DropdownSourceSmartCard')}
			>
				<TitleBlock maxLines={1} size={SmartLinkSize.Medium} />
				<SnippetBlock maxLines={2} />
			</Card>
		</ErrorBoundary>
	);
}

/**
 * @deprecated Use KnowledgeSmartCard from @atlassian/lumina instead.
 *
 * @example
 * import { KnowledgeSmartCard } from '@atlassian/lumina';
 */
export const KnowledgeSmartCard = ({
	source,
	variant,
	index,
	searchSessionId,
	onClickHandler,
}: KnowledgeSmartCardProps): React.JSX.Element => {
	// Apply defaults for internal components
	const internalProps: SmartCardInternalProps = {
		source,
		index: index || 0,
		searchSessionId: searchSessionId || '',
		onClickHandler: onClickHandler || (() => {}),
	};

	if (variant === CardVariant.COMPACT) {
		return <CompactKnowledgeSmartCard {...internalProps} />;
	}

	if (variant === CardVariant.DROPDOWN_SOURCE) {
		return <DropdownSourceSmartCard {...internalProps} />;
	}

	if (variant === CardVariant.ACTION_CARD) {
		return <ActionCard {...internalProps} />;
	}

	const shouldUseEmbedded = source.product && source.product in EMBEDDED_SOURCE_PRODUCTS;

	if (shouldUseEmbedded) {
		return <EmbeddedKnowledgeSmartCard {...internalProps} />;
	}

	return <StandardKnowledgeSmartCard {...internalProps} />;
};
